import styled from 'styled-components';

import { ClienteProdutoDetail } from '../../../../../../../types/cliente-produto';
import { formatPreco } from '../format';
import { CollapsibleSection } from './collapsible-section';

interface Props {
  data: ClienteProdutoDetail;
}

const SubBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 10px;

  h4 {
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textMuted};
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 13.5px;
  }

  li {
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  small {
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: 12px;
    margin-left: 8px;
  }
`;

const Totals = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 6px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const TotalChip = styled.div`
  background: ${({ theme }) => theme.colors.bgMuted};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong {
    font-size: 18px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  small {
    font-size: 11px;
    color: ${({ theme }) => theme.colors.textMuted};
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
`;

export const RelacoesSection = ({ data }: Props) => {
  const moedaSigla =
    (data.comercial && (data.comercial.moedaSigla as string | null)) ?? 'BRL';

  return (
    <CollapsibleSection
      title="Ofertas, cupons, links e kit"
      testId="produto-section-relacoes"
    >
      <Totals>
        <TotalChip>
          <strong>{data.totais.ofertas}</strong>
          <small>Ofertas</small>
        </TotalChip>
        <TotalChip>
          <strong>{data.totais.cupons}</strong>
          <small>Cupons</small>
        </TotalChip>
        <TotalChip>
          <strong>{data.totais.links}</strong>
          <small>Links</small>
        </TotalChip>
        <TotalChip>
          <strong>{data.totais.componentesKit}</strong>
          <small>Componentes do kit</small>
        </TotalChip>
        <TotalChip>
          <strong>{data.totais.planos}</strong>
          <small>Planos</small>
        </TotalChip>
      </Totals>

      {data.ofertas.length > 0 && (
        <SubBlock>
          <h4>Ofertas</h4>
          <ul>
            {data.ofertas.map((o) => (
              <li key={o.id}>
                {o.nome ?? '—'}
                <small>{formatPreco(o.preco, moedaSigla)}</small>
              </li>
            ))}
          </ul>
        </SubBlock>
      )}

      {data.cupons.length > 0 && (
        <SubBlock>
          <h4>Cupons</h4>
          <ul>
            {data.cupons.map((c) => (
              <li key={c.id}>
                {c.codigo ?? '—'}
                {c.desconto != null && <small>desconto {c.desconto}</small>}
              </li>
            ))}
          </ul>
        </SubBlock>
      )}

      {data.links.length > 0 && (
        <SubBlock>
          <h4>Links de venda</h4>
          <ul>
            {data.links.map((l) => (
              <li key={l.id}>
                {l.descricao ?? l.id}
                {l.tipo != null && <small>tipo {l.tipo}</small>}
                {l.ativo === false && <small>(inativo)</small>}
              </li>
            ))}
          </ul>
        </SubBlock>
      )}

      {data.componentesDoKit.length > 0 && (
        <SubBlock>
          <h4>Componentes do kit</h4>
          <ul>
            {data.componentesDoKit.map((k) => (
              <li key={k.id}>
                Produto {k.produtoId ?? '—'}
                <small>quantidade {k.quantidade ?? 1}</small>
              </li>
            ))}
          </ul>
        </SubBlock>
      )}
    </CollapsibleSection>
  );
};
